import { useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";

const DEFAULT_COLORS = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff7f50",
  "#8dd1e1",
  "#a4de6c",
  "#d0ed57",
  "#d88884",
  "#a28fd0",
  "#ffbb28",
  "#ff99a1",
  "#6ec1e4",
  "#b5e48c",
  "#c699ff",
  "#ffb066",
];

const normalizePieData = (data) => {
  if (!data || data.length === 0) return [];
  const total = data.reduce((sum, item) => sum + item.sent, 0);
  if (total === 0) {
    return data.map((item) => ({ ...item, displayPercent: 0 }));
  }
  return data.map((item) => ({
    ...item,
    displayPercent: (item.sent / total) * 100,
  }));
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const item = payload[0].payload;
    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid #ccc",
          padding: "8px 12px",
          borderRadius: 4,
        }}
      >
        <p style={{ margin: 0, fontWeight: "bold" }}>
          {item.code} ({item.sent})
        </p>
      </div>
    );
  }
  return null;
};

const CustomLegend = ({ payload, data, colors, activeIndex, onHover, onHoverEnd }) => (
  <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
    {payload.map((_, index) => {
      const item = data[index];
      const isActive = activeIndex === index;
      const isDimmed = activeIndex !== null && !isActive;
      return (
        <li
          key={`item-${index}`}
          onMouseEnter={() => onHover(index)}
          onMouseLeave={onHoverEnd}
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: 6,
            fontSize: 18,
            cursor: "pointer",
            opacity: isDimmed ? 0.3 : 1,
            fontWeight: isActive ? "bold" : "normal",
          }}
        >
          <span
            style={{
              width: 12,
              height: 12,
              backgroundColor: colors[index % colors.length],
              display: "inline-block",
              marginRight: 8,
              boxShadow: isActive ? "0 0 0 3px rgba(0,0,0,0.15)" : "none",
            }}
          />
          <span>{item?.code} ({item?.sent})</span>
        </li>
      );
    })}
  </ul>
);

export default function PieChartComponent({
  year,
  data,
  colors,
  chartWidth = 850,
  chartHeight = 500,
  innerRadius = 90,
  outerRadius = 180,
  labelX = "42%",
}) {
  const resolvedColors = colors || DEFAULT_COLORS;
  const [activeIndex, setActiveIndex] = useState(null);

  const totalSent = data.reduce((sum, item) => sum + item.sent, 0);

  return (
    <ResponsiveContainer width={chartWidth} height={chartHeight}>
      <PieChart style={{ marginBottom: "1rem", overflow: "visible" }}>
        <Pie
          data={normalizePieData(data)}
          dataKey="displayPercent"
          nameKey="code"
          cx="50%"
          cy="50%"
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          onMouseEnter={(_, index) => setActiveIndex(index)}
          onMouseLeave={() => setActiveIndex(null)}
        >
          {data.map((_, index) => (
            <Cell
              key={index}
              fill={resolvedColors[index % resolvedColors.length]}
              opacity={activeIndex === null ? 1 : activeIndex === index ? 1 : 0.3}
            />
          ))}
        </Pie>

        <Tooltip content={<CustomTooltip />} />

        <text
          x={labelX}
          y="50%"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={18}
          fill="#333"
        >
          <tspan x={labelX} dy="-0.5em">Totale {year}:</tspan>
          <tspan x={labelX} dy="1.2em" fontWeight={600}>{totalSent}</tspan>
        </text>

        <Legend
          layout="vertical"
          verticalAlign="middle"
          align="right"
          content={
            <CustomLegend
              data={data}
              colors={resolvedColors}
              activeIndex={activeIndex}
              onHover={setActiveIndex}
              onHoverEnd={() => setActiveIndex(null)}
            />
          }
        />
      </PieChart>
    </ResponsiveContainer>
  );
}
